// This file is automatically generated. Do not edit it directly.
import { createClient } from "@supabase/supabase-js";
import { guardUndefinedFilters } from "@/lib/supabase/guardUndefinedFilters";
import type { Database } from "./types";
import { createDemoAwareFetch } from "@/lib/demo/backend";

const SUPABASE_URL =
  import.meta.env.VITE_SUPABASE_URL ||
  (import.meta.env.DEV ? "https://placeholder.supabase.co" : "");
const SUPABASE_PUBLISHABLE_KEY =
  import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY ||
  (import.meta.env.DEV ? "placeholder-anon-key" : "");

if (!SUPABASE_URL || !SUPABASE_PUBLISHABLE_KEY) {
  console.error("[Supabase Client] Missing environment variables!");
}

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";

export const supabase = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
  auth: {
    storage: localStorage,
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    flowType: "pkce",
  },
  // Sample-data mode answers company data in the browser and refuses writes
  // (src/lib/demo/backend.ts); otherwise this is the plain fetch.
  // guardUndefinedFilters: a query that runs before its id is known (`eq.undefined`) never
  // reaches the API (src/lib/supabase/guardUndefinedFilters.ts).
  global: { fetch: guardUndefinedFilters(createDemoAwareFetch(SUPABASE_URL)) },
});

// Set up automatic token refresh with rotation
let refreshTimer: NodeJS.Timeout | null = null;

function setupTokenRefresh() {
  // Clear existing timer
  if (refreshTimer) {
    clearTimeout(refreshTimer);
  }

  // Get current session
  supabase.auth.getSession().then(({ data: { session } }) => {
    if (session) {
      // Calculate time until expiry (with 5 minute buffer)
      const expiresAt = session.expires_at || 0;
      const now = Math.floor(Date.now() / 1000);
      const timeUntilExpiry = expiresAt - now - 300; // 5 minute buffer

      if (timeUntilExpiry > 0) {
        // Set timer to refresh token before expiry
        refreshTimer = setTimeout(async () => {
          const {
            data: { session: newSession },
            error,
          } = await supabase.auth.refreshSession();

          if (error) {
            console.error("Token refresh error:", error);
            // If refresh fails, user will need to log in again
            supabase.auth.signOut();
          } else if (newSession) {
            // Recursively set up next refresh
            setupTokenRefresh();
          }
        }, timeUntilExpiry * 1000);
      } else {
        // Session already expired or expiring soon, refresh immediately
        supabase.auth.refreshSession();
      }
    }
  });
}

// Set up token refresh on initialization
setupTokenRefresh();

// Listen for auth state changes to reset timer
supabase.auth.onAuthStateChange((event, session) => {
  if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED") {
    setupTokenRefresh();
  } else if (event === "SIGNED_OUT") {
    if (refreshTimer) {
      clearTimeout(refreshTimer);
      refreshTimer = null;
    }
  }
});
